import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Fern from '../../Assets/fern2.png';
import qrCode from "../../Assets/landing/goals/qr2.jpeg";

const AICreateProject = ({ onProjectCreated, token }) => {
    const [prompt, setPrompt] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
    const [checkingSubscription, setCheckingSubscription] = useState(false);
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [generatedData, setGeneratedData] = useState(null);
    const navigate = useNavigate();
    let authToken = localStorage.getItem("token");

    // Check subscription status
    const checkSubscription = async () => {
        if (!authToken) {
            setHasActiveSubscription(false);
            return false;
        }

        setCheckingSubscription(true);
        try {
            const response = await axios.get(
                '/billing/subscription-status',
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        'Accept': 'application/json',
                    },
                }
            );
            
            const apiData = response.data?.data;
            const hasAccess = apiData?.has_active_subscription === true;
            
            setHasActiveSubscription(hasAccess);
            setSubscriptionData(apiData);
            return hasAccess;
        } catch (error) {
            console.error('Subscription check failed:', error);
            setHasActiveSubscription(false);
            return false;
        } finally {
            setCheckingSubscription(false);
        }
    };

    // Check subscription when modal opens
    const handleOpenModal = async () => {
        const hasSubscription = await checkSubscription();
        
        if (hasSubscription) {
            setShowModal(true);
        } else {
            setShowSubscriptionModal(true);
        }
    };

    const handleAICreate = async () => {
        // Double-check subscription before proceeding
        const hasSubscription = await checkSubscription();
        if (!hasSubscription) {
            setShowSubscriptionModal(true);
            toast.error('AI project generation requires an active subscription. Please subscribe to continue.', {
                position: "bottom-right",
                autoClose: 3000,
            });
            return;
        }

        if (!prompt.trim() || prompt.length < 5) {
            toast.warning('Please describe your project idea (minimum 5 characters)', {
                position: "bottom-right",
                autoClose: 2000,
            });
            return;
        }

        setIsGenerating(true);
        try {
            const response = await axios.post('/projects/ai-create', {
                prompt: prompt
            }, {
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                }
            });

            setGeneratedData(response.data);
            setShowModal(false);
            
            if (onProjectCreated) {
                onProjectCreated(response.data);
            }
            
            // Get the project data from response
            const projectData = response.data?.data;
            const project = projectData?.frond || projectData?.project;
            const projectId = project?.id;
            const planSlug = response.data?.subscription?.plan_slug || subscriptionData?.plan_slug;
            const isPremium = planSlug === 'premium';
            const unlimitedProjects = response.data?.subscription?.unlimited_projects === true || isPremium;
            
            // Get credit info (only relevant for non-premium)
            const creditsUsed = response.data?.credits?.cost || 0;
            const creditsRemaining = response.data?.credits?.remaining || 0;
            const projectTitle = project?.title || projectData?.title || 'Project';
            
            if (projectId) {
                // Show appropriate toast based on plan
                if (isPremium || unlimitedProjects) {
                    // Premium user - unlimited projects, no credit deduction
                    toast.success(`✨ Project "${projectTitle}" created successfully!`, {
                        position: "bottom-right",
                        autoClose: 3000,
                        icon: "🚀"
                    });
                } else {
                    // Starter or Pro user - show credit usage
                    toast.success(
                        `✅ Project "${projectTitle}" created successfully!\n\n` +
                        `📊 Credits used: ${creditsUsed}\n` +
                        `💎 Credits remaining: ${creditsRemaining}\n\n` +
                        `Your plan resets monthly. Upgrade to Premium for unlimited projects!`,
                        {
                            position: "bottom-right",
                            autoClose: 5000,
                            icon: "✨"
                        }
                    );
                }
                
                // Extract location coordinates from the response
                const locationData = {
                    lat: project?.latitude || projectData?.latitude,
                    lng: project?.longitude || projectData?.longitude,
                    address: project?.address || projectData?.address,
                    name: projectTitle
                };
                
                // Navigate with location data in state
                navigate(`/frond/about/${projectId}`, { 
                    state: { 
                        focusOnMap: true,
                        newProjectLocation: locationData,
                        shouldFocusLocation: true
                    }
                });
            } else {
                toast.success('Project created successfully!', {
                    position: "bottom-right",
                    autoClose: 2000,
                });
            }
            
        } catch (error) {
            console.error('AI creation failed:', error);
            
            // Handle specific error cases
            if (error.response?.status === 429) {
                const errorMessage = error.response?.data?.message || 'Insufficient credits. Please wait for your monthly reset or upgrade your plan.';
                toast.error(errorMessage, {
                    position: "bottom-right",
                    autoClose: 4000,
                });
                setShowSubscriptionModal(true);
            } else if (error.response?.status === 403) {
                toast.error('Subscription required. Please subscribe to use AI features.', {
                    position: "bottom-right",
                    autoClose: 3000,
                });
                setShowSubscriptionModal(true);
            } else {
                toast.error(error.response?.data?.message || 'Failed to create project', {
                    position: "bottom-right",
                    autoClose: 3000,
                });
            }
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <>
            {/* AI Create Button - Beautiful Card Design */}
            <div className="relative overflow-hidden rounded-2xl mb-8 shadow-xl transition-all duration-300 hover:shadow-2xl">
                {/* Animated Gradient Background */}
                <div className="absolute inset-0 bg-gradient-to-r from-rose-700 via-pink-500 to-orange-600 animate-gradient bg-[length:200%_200%]"></div>
                
                {/* Content */}
                <div className="relative bg-white/10 backdrop-blur-sm p-8 text-center">
                    <div className="flex items-center justify-center mb-4">
                        <h3 className="text-2xl font-bold text-white mb-2">
                            Create a Project with Fern AI
                        </h3>
                        <img src={Fern} alt="AI Create Project" className="w-15 h-10" />
                    </div>
                    <p className="text-white/90 text-sm mb-6 max-w-md mx-auto">
                        Let AI help you create a complete project plan in seconds
                    </p>
                    
                    <button 
                        className="group relative inline-flex items-center gap-2 bg-white text-pink-500 px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                        onClick={handleOpenModal}
                        disabled={checkingSubscription}
                    >
                        <span className="text-xl">✨</span>
                        <span>{checkingSubscription ? 'Checking...' : 'Start Creating'}</span>
                        <span className="absolute right-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300">→</span>
                    </button>
                    
                    <p className="text-white/70 text-xs mt-4">
                        Describe your project in natural language
                    </p>
                </div>
            </div>

            {/* AI Modal - Beautiful Design */}
            {showModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center mt-12 z-[1000] animate-fadeIn">
                    <div className="bg-white rounded-2xl max-w-2xl w-[90%] max-h-[95vh] overflow-hidden shadow-2xl animate-slideUp">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-purple-600 to-pink-500 px-6 py-4">
                            <div className="flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                                        <span className="text-xl">✨</span>
                                    </div>
                                    <div className="flex items-center justify-center">
                                        <h3 className="text-2xl font-bold text-white mb-2">
                                            Create a Project with Fern AI
                                        </h3>
                                        <img src={Fern} alt="AI Create Project" className="w-15 h-10" />
                                    </div>
                                </div>
                                <button 
                                    className="text-white/80 hover:text-white text-3xl transition-colors"
                                    onClick={() => setShowModal(false)}
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6">
                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Describe your project idea:
                                </label>
                                <textarea
                                    className="w-full p-4 border-2 border-gray-200 rounded-xl text-sm font-inherit resize-vertical focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                                    placeholder="Example: Plan a 5-day trip to Hurghada for a family of 4 with a budget of $1500. 
                                    We want all-inclusive resort, giftun island snorkeling, and desert quad biking at the end of Sep."
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    rows={5}
                                />
                                <div className="flex justify-between items-center mt-2">
                                    <div className="text-xs text-gray-400">
                                        {prompt.length}/1000 characters
                                    </div>
                                    {prompt.length > 0 && prompt.length < 5 && (
                                        <div className="text-xs text-orange-500">
                                            Minimum 5 characters required
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* Example Prompts */}
                            <div className="mb-4">
                                <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                    <span className="text-lg">💡</span>
                                    Try these examples:
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <button 
                                        className="bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 border border-purple-200 px-4 py-2 rounded-full text-xs font-medium text-purple-700 transition-all duration-200 hover:scale-105"
                                        onClick={() => setPrompt("Plan a 5-day Nile cruise from Luxor to Aswan with visits to ancient temples, hot air balloon ride over Valley of the Kings, and a traditional felucca sunset sail")}
                                    >
                                        🛶 Nile Cruise Adventure
                                    </button>
                                    <button 
                                        className="bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 border border-purple-200 px-4 py-2 rounded-full text-xs font-medium text-purple-700 transition-all duration-200 hover:scale-105"
                                        onClick={() => setPrompt("Create a project for my friend's wedding in June 2026, need to plan venue, catering, and guest list for 100 people")}
                                    >
                                        💍 Wedding planning
                                    </button>
                                    <button 
                                        className="bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 border border-purple-200 px-4 py-2 rounded-full text-xs font-medium text-purple-700 transition-all duration-200 hover:scale-105"
                                        onClick={() => setPrompt("Home renovation project for my kitchen in Cairo, budget $5000, want modern style with new appliances")}
                                    >
                                        🏠 Home renovation
                                    </button>
                                </div>
                            </div>

                            {/* Features Grid */}
                            <div className="grid grid-cols-3 gap-3 mb-3">
                                <div className="text-center p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl">
                                    <div className="text-2xl mb-1">🤖</div>
                                    <div className="text-xs font-medium text-gray-600">AI-Powered</div>
                                </div>
                                <div className="text-center p-3 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
                                    <div className="text-2xl mb-1">⚡</div>
                                    <div className="text-xs font-medium text-gray-600">Fast Creation</div>
                                </div>
                                <div className="text-center p-3 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl">
                                    <div className="text-2xl mb-1">🎯</div>
                                    <div className="text-xs font-medium text-gray-600">Smart Planning</div>
                                </div>
                            </div>
                            
                            {/* Credit Info based on plan */}
                            {subscriptionData?.plan_slug === 'premium' ? (
                                <div className="mt-4 p-3 bg-purple-50 rounded-xl border border-purple-200">
                                    <p className="text-xs text-purple-600 text-center">
                                        ✨ Premium Plan: Unlimited project creation! No credits deducted for projects.
                                    </p>
                                </div>
                            ) : (
                                <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-200">
                                    <p className="text-xs text-blue-600 text-center">
                                        💡 Each project creation costs 1 credit. Your remaining credits will be shown after creation.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="flex justify-end gap-3 px-8 py-4 bg-gray-50 border-t border-gray-100">
                            <button 
                                className="px-6 py-2 rounded-lg font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-all duration-200"
                                onClick={() => setShowModal(false)}
                            >
                                Cancel
                            </button>
                            <button 
                                className="relative overflow-hidden bg-gradient-to-r from-purple-600 to-pink-500 text-white px-6 py-2 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-all duration-200 group"
                                onClick={handleAICreate}
                                disabled={isGenerating || !prompt.trim() || prompt.length < 5}
                            >
                                <span className="relative z-10 flex items-center gap-2">
                                    {isGenerating ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            ✨ Create Project
                                            <span className="absolute right-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300">→</span>
                                        </>
                                    )}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Subscription Required Modal with QR Code */}
            {showSubscriptionModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center mt-12 z-[99999] animate-fadeIn">
                    <div className="bg-white rounded-4xl max-w-xl w-[90%] overflow-hidden shadow-2xl animate-slideUp">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-orange-500 to-pink-500 px-6 py-4">
                            <div className="flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                                        <span className="text-xl">📱</span>
                                    </div>
                                    <h3 className="text-xl font-bold text-white">
                                        Subscription Required
                                    </h3>
                                </div>
                                <button 
                                    className="text-white/80 hover:text-white text-3xl transition-colors"
                                    onClick={() => setShowSubscriptionModal(false)}
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 text-center">
                            <h3 className="text-xl font-bold text-gray-800 mb-2">
                                AI Features Require Subscription
                            </h3>
                            
                            <p className="text-gray-600 text-sm mb-6">
                                To use AI project creation and other premium features, 
                                you need an active subscription. Subscribe through our mobile app to get started!
                            </p>

                            <div className='flex gap-4 rounded-xl flex-row bg-gradient-to-r from-purple-50 to-pink-50'>
                                <div className="mt-10 p-4 mb-6 text-left">
                                    <p className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                        <span>🎯</span> What you get:
                                    </p>
                                    <ul className="space-y-2 text-sm text-gray-600">
                                        <li className="flex items-center gap-2">
                                            <span className="text-green-500">✓</span> AI project creation
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <span className="text-green-500">✓</span> AI task generation
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <span className="text-green-500">✓</span> AI image generation
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <span className="text-green-500">✓</span> Chat with Fern AI (Premium Plan)
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <span className="text-green-500">✓</span> Priority support
                                        </li>
                                    </ul>
                                </div>

                                {/* QR Code Section */}
                                <div className="mb-6">
                                    <p className="text-sm font-semibold text-gray-700 mb-3 mt-4 flex items-center justify-center gap-2">
                                        Scan to download the app
                                    </p>
                                    <div className="flex justify-center">
                                        <div className="bg-white p-2 rounded-2xl shadow-lg inline-block">
                                            <img 
                                                src={qrCode} 
                                                alt="Download Fern App" 
                                                className="w-48 h-48 object-contain"
                                            />
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-3">
                                        Available on iOS and Android
                                    </p>
                                </div>
                            </div>

                            <div className="border-t border-gray-100 pt-4">
                                <p className="text-xs text-gray-400">
                                    Already have a subscription? Make sure you're logged in with the same account.
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-center gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
                            <button 
                                className="px-6 py-2 rounded-lg font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-all duration-200"
                                onClick={() => setShowSubscriptionModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                @keyframes gradient {
                    0% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                
                @keyframes slideUp {
                    from { 
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to { 
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .animate-gradient {
                    animation: gradient 3s ease infinite;
                }
                
                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-out;
                }
                
                .animate-slideUp {
                    animation: slideUp 0.4s ease-out;
                }
            `}</style>
        </>
    );
};

export default AICreateProject;